import { useToast } from "@/hooks/utils/useToast";
import i18n from "@/i18n";
import { isMacOSPlatform, isTauri } from "@/lib/utils/tauriUtils";
import { useEffect, useRef, useState } from "react";
import SpeechRecognition from "react-speech-recognition";

// Handles the WKWebView-specific "aborted" error that react-speech-recognition
// silently swallows. Must only be used inside a Tauri desktop build; on the web
// the useEffect exits immediately via the isTauri() guard.
//
// WKWebView always fires an "aborted" error on the very first start() while the
// macOS Dictation service initialises. We retry once after 300ms. A second
// "aborted" means Dictation is genuinely disabled, so we surface a toast and
// set isDictationDisabled so the UI can reflect the state.
const useTauriSpeechErrorHandler = (startListenSession: () => void) => {
    const { toast } = useToast();
    // Evaluated once — platform never changes during a session.
    const isMacOS = isMacOSPlatform();
    // Keep the callback reference fresh so the mount-time closure never goes stale.
    const startListenRef = useRef(startListenSession);
    const hasRetriedAbortRef = useRef(false);

    const [isDictationDisabled, setIsDictationDisabled] = useState(false);

    useEffect(() => {
        startListenRef.current = startListenSession;
    });

    const resetDictationDisabled = () => {
        setIsDictationDisabled(false);
        hasRetriedAbortRef.current = false;
    };

    useEffect(() => {
        if (!isTauri()) return;

        const manager = (
            SpeechRecognition as unknown as {
                getRecognitionManager?: () => { recognition?: EventTarget };
            }
        ).getRecognitionManager?.();
        const recognition = manager?.recognition;
        if (!recognition) return;

        const handleError = (event: Event) => {
            if ((event as SpeechRecognitionErrorEvent).error !== "aborted")
                return;
            if (!hasRetriedAbortRef.current) {
                hasRetriedAbortRef.current = true;
                setTimeout(() => startListenRef.current(), 300);
            } else {
                hasRetriedAbortRef.current = false;
                setIsDictationDisabled(true);
                const toastKey = isMacOS
                    ? "global.audioCapture.dictationDisabledMac"
                    : "global.audioCapture.dictationDisabledWindows";
                toast({ title: i18n.t(toastKey), variant: "error" });
            }
        };

        recognition.addEventListener("error", handleError);
        return () => recognition.removeEventListener("error", handleError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { isDictationDisabled, resetDictationDisabled };
};

export default useTauriSpeechErrorHandler;
